/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useState, useEffect, useCallback} from 'react';
import {useParams} from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import {Helmet} from 'react-helmet-async';
import {Box, CardContent, FormControl, Grid, Checkbox} from '@mui/material';
import {Formik} from 'formik';
import * as Yup from 'yup';

import {USER_TYPE, USER_ROLE} from '../../../constants/UserType';
import {path} from '../../../routes/path';
import {typeRoutes, DataDetail} from '../../../types/typeSDCAdministrators';
import IslanderService from '../../../services/Islander/islander.service';
import AdministratorsService from '../../../services/Administrators/administrators.service';
import ResetPasswordService from '../../../services/User/reset_password.service';
import {Input, Breadcrumbs, Select, FooterDetail} from '../../../components';
import success from '../../../constants/success';
import {AllModal} from './SDCAdministratorsDetail.method';

// styles
import {
  Card,
  Divider,
  Shadow,
  Typography,
  StyledDivBreadcrumbs,
} from './SDCAdministratorsDetail.style';
import {
  rbacDepartments,
  rbacPermissions,
} from '../../../constants/roleBasedAccessControlConstants';
import AccessRoleService from '../../../services/AccessRoles/AccessRole.service';
import {isEmpty} from 'lodash';

function SDCAdministratorsDetail() {
  const paramsUrl = useParams();
  const idDetail = paramsUrl?.id;
  const {showNotification, user, checkRbac} = useAuth();

  // states
  const [isGettingIslanderPartners, setIsGettingIslanderPartners] =
    useState<boolean>(true);
  const [isGettingAdminRoles, setIsGettingAdminRoles] = useState<boolean>(true);
  const [isGettingDataDetails, setIsGettingDataDetails] =
    useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);
  const [optionIslandPartners, setOptionIslandPartners] = useState<any>([]);
  const [dataDetails, setDataDetails] = useState<any>({});
  const [optionAdminRoles, setOptionAdminRoles] = useState<any>([]);
  const [disabledRoleOptions, setDisabledRoleOptions] =
    useState<boolean>(false);
  const [isDefaultFreeze, setIsDefaultFreeze] = useState(false);
  const [islanderPartnerQueryParams] = useState({
    _page: 0,
    _size: 99999, // temp, check with be team if there is a value for all items
    status_eq: 1,
    _sort: 'name:asc',
  });
  const [adminRolesQueryParams] = useState({
    _page: 0,
    _size: 99999, // temp, check with be team if there is a value for all items
    status_eq: 1,
    _sort: 'id:asc',
  });
  const [initialValues, setInitialValues] = useState<DataDetail>({
    firstName: '',
    lastName: '',
    designation: '',
    email: '',
    type: USER_TYPE.ADMINISTRATOR,
    merchantId: '',
    roleId: '',
    isFreeze: false,
    phoneNumber: '',
  });
  const [modal, setModal] = useState({
    isModalConfirm: false,
    isModalConfirmDelete: false,
    isModalSuccess: false,
    isModalSuccessDelete: false,
    isModalEditTier: false,
    isModalEditPoints: false,
  });

  useEffect(() => {
    setIsLoading(
      isGettingIslanderPartners || isGettingAdminRoles || isGettingDataDetails
    );
  }, [isGettingIslanderPartners, isGettingAdminRoles, isGettingDataDetails]);

  const toggleModalConfirm = async () => {
    setModal(preState => ({
      ...preState,
      isModalConfirm: !modal.isModalConfirm,
    }));
  };

  const toggleModalConfirmDelete = () => {
    setModal(preState => ({
      ...preState,
      isModalConfirmDelete: !modal.isModalConfirmDelete,
    }));
  };

  const toggleModalSuccess = async () => {
    setIsLoading(true);
    await new ResetPasswordService()
      .post('requestResetPassword', {email: initialValues?.email})
      .then(res => {
        setIsLoading(false);
        if (res?.success) {
          toggleModalConfirm();
          setModal(preState => ({
            ...preState,
            isModalSuccess: !modal.isModalSuccess,
          }));
        } else {
          showNotification({
            message: 'Reset password failed! Please try again later',
            variation: 'error',
          });
        }
      });
  };

  const toggleModalSuccessDelete = () => {
    setIsLoading(true);
    new AdministratorsService().softDelete(idDetail).then(res => {
      if (res && res?.success) {
        setIsLoading(false);
        toggleModalConfirmDelete();
        setModal(preState => ({
          ...preState,
          isModalSuccessDelete: !modal.isModalSuccessDelete,
        }));
      }
    });
  };

  const onRequestUpdate = newParams => {
    new AdministratorsService().update(idDetail, newParams).then(res => {
      if (res.success) {
        getDataDetail();
        setIsLoading(false);
        showNotification({
          message: success.UPDATE_SUCCESS,
        });
      } else {
        setIsLoading(false);
        showNotification({
          message: res.errorMessage,
          variation: 'error',
        });
      }
    });
  };

  const onEdit = values => {
    setIsLoading(true);

    const newParams = {
      ...values,
      merchantId:
        values?.type.toString() === USER_TYPE.MERCHANT.toString()
          ? values.merchantId
          : '',
      islandPartner:
        values?.type.toString() === USER_TYPE.MERCHANT.toString()
          ? values?.islandPartner
          : '',
      roleId:
        values?.type.toString() === USER_TYPE.ADMINISTRATOR.toString()
          ? values?.roleId
          : '',
      phoneNumber: values.phoneNumber.replace('+', ''),
    };

    if (!values?.isFreeze && isDefaultFreeze) {
      return onRequestUpdate({
        ...newParams,
        lastUnfreeze: new Date().toISOString(),
      });
    } else {
      return onRequestUpdate(newParams);
    }
  };

  const getIslanderPartners = async () => {
    setIsGettingIslanderPartners(true);
    await new IslanderService().getAll(islanderPartnerQueryParams).then(res => {
      if (res?.data && Array.isArray(res.data.items)) {
        const items = res?.data.items.map(item => {
          return {
            label: item.name,
            value: item.id,
          };
        });
        setOptionIslandPartners(items);
        setIsGettingIslanderPartners(false);
      }
    });
  };

  const getAdminRoles = async () => {
    setIsGettingAdminRoles(true);
    await new AccessRoleService().getAll(adminRolesQueryParams).then(res => {
      if (res?.data && Array.isArray(res.data.items)) {
        const items = res?.data.items.map(item => {
          return {
            roleKey: item.roleKey,
            label: item.roleName,
            value: item.id,
          };
        });
        setOptionAdminRoles(items);
        setIsGettingAdminRoles(false);
      }
    });
  };

  const getSelectedRoleIdByRoleName = useCallback(
    (roleName?: string, defaultValue = '') => {
      if (!roleName) return defaultValue;
      const selectedRole = optionAdminRoles.find(
        option => option.label === roleName
      );
      return selectedRole?.value ?? defaultValue;
    },
    [optionAdminRoles]
  );

  const getDataDetail = useCallback(() => {
    setIsGettingDataDetails(true);
    new AdministratorsService().getOne(idDetail).then(res => {
      if (res && res.data && res.data.data) {
        const data = res?.data?.data;
        setIsGettingDataDetails(false);
        setIsDefaultFreeze(res?.data?.data?.isFreeze);
        setDataDetails(data);
        setInitialValues({
          ...initialValues,
          firstName: data?.firstName,
          lastName: data?.lastName,
          email: data?.email,
          type: data.type,
          merchantId: data?.merchantId ?? '',
          roleId: getSelectedRoleIdByRoleName(data.roleName),
          designation: data?.designation,
          isFreeze: data?.isFreeze,
          phoneNumber: data?.phoneNumber ?? '',
        });
      }
    });
  }, [optionAdminRoles]);

  useEffect(() => {
    getAdminRoles();
    getIslanderPartners();
  }, []);

  useEffect(() => {
    if (!isGettingAdminRoles && !isGettingIslanderPartners) {
      getDataDetail();
    }
  }, [isGettingAdminRoles, isGettingIslanderPartners]);

  /**
   * Disable role options if the current logged in admin is not Super Admin and the detail SDC Admin is Super Admin
   * Only Super Admin can edit another Super Admin account
   */
  useEffect(() => {
    if (!isEmpty(dataDetails) && !isEmpty(optionAdminRoles)) {
      const isLoggedInUserSuperAdmin = user?.accessRoleDepartments?.includes(
        rbacDepartments.SUPER_ADMIN
      );
      if (!isLoggedInUserSuperAdmin) {
        // If the current logged in admin is not Super Admin
        const roleIdFromDataDetails = getSelectedRoleIdByRoleName(
          dataDetails.roleName
        );
        const optionSuperAdmin = optionAdminRoles.find(
          option => option.roleKey === rbacDepartments.SUPER_ADMIN
        );
        const isCurrentSDCAdminSuperAdmin =
          optionSuperAdmin && roleIdFromDataDetails === optionSuperAdmin?.value;
        if (isCurrentSDCAdminSuperAdmin) {
          // If the current detail SDC Admin is Super Admin, disable the role options
          if (disabledRoleOptions !== true) {
            setDisabledRoleOptions(true);
          }
        } else if (optionSuperAdmin) {
          // If the current detail SDC Admin is not Super Admin, remove Super Admin from the role options
          const filteredOptions = optionAdminRoles.filter(
            option => option.roleKey !== rbacDepartments.SUPER_ADMIN
          );
          setOptionAdminRoles(filteredOptions);
          setDisabledRoleOptions(false);
        }
      }
    }
  }, [dataDetails, optionAdminRoles, disabledRoleOptions]);

  return (
    <>
      <Helmet title="Edit Administrator Detail" />

      <AllModal
        isModalConfirm={modal.isModalConfirm}
        toggleModalConfirm={toggleModalConfirm}
        isModalConfirmDelete={modal.isModalConfirmDelete}
        isModalSuccessDelete={modal.isModalSuccessDelete}
        isModalSuccess={modal.isModalSuccess}
        toggleModalConfirmDelete={toggleModalConfirmDelete}
        toggleModalSuccessDelete={toggleModalSuccessDelete}
        toggleModalSuccess={toggleModalSuccess}
        setModal={setModal}
        isLoading={isLoading}
      />

      <Box
        component="section"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h3" gutterBottom display="inline">
          Portal User Detail
        </Typography>

        <StyledDivBreadcrumbs>
          <Breadcrumbs
            routes={[
              typeRoutes(
                'Portal Users',
                checkRbac({
                  permissionString:
                    rbacPermissions.ADMINISTRATORS__LISTING__VIEW,
                })
                  ? `/${path.PORTAL_USERS}`
                  : undefined
              ),
              typeRoutes('Detail', null, true),
            ]}
          />
        </StyledDivBreadcrumbs>

        <Divider my={6} />

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={Yup.object().shape({
            email: Yup.string()
              .email('Must be a valid email')
              .max(255)
              .required('Email is required'),
            firstName: Yup.string().required('First name is required'),
            lastName: Yup.string().required('Last name is required'),
            merchantId: Yup.string().when('type', {
              is: typeValue =>
                typeValue.toString() === USER_TYPE.MERCHANT.toString(),
              then: Yup.string().required('Island Partner is required'),
            }),
            roleId: Yup.string().when('type', {
              is: typeValue =>
                typeValue.toString() === USER_TYPE.ADMINISTRATOR.toString(),
              then: Yup.string().required('Role is required'),
            }),
            phoneNumber: Yup.string()
              .required('Phone number is required')
              .matches(
                /[6|8|9]\d{7}|\+65[6|8|9]\d{7}|\+65\s[6|8|9]\d{7}/,
                'Phone number is invalid'
              ),
          })}
          onSubmit={onEdit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            setValues,
            // isSubmitting,
            touched,
            values,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container mb="30px">
                <Grid item xs={12} lg={12}>
                  <Shadow>
                    <Card px={6} pt={6}>
                      <CardContent>
                        <Grid
                          container
                          rowSpacing={0}
                          columnSpacing={10}
                          mb="50px"
                        >
                          <Grid item xs={12} mb="30px">
                            <Typography variant="h5">
                              Administrator information
                            </Typography>
                          </Grid>

                          <Grid item md={6}>
                            <FormControl variant="standard" fullWidth>
                              <Input
                                type="text"
                                name="firstName"
                                label="First Name"
                                value={values.firstName}
                                helperText={
                                  touched.firstName && errors.firstName
                                }
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.firstName && errors.firstName
                                )}
                                className="mandatory-field"
                              />
                            </FormControl>
                          </Grid>
                          <Grid item md={6}>
                            <FormControl variant="standard" fullWidth>
                              <Input
                                type="text"
                                name="lastName"
                                label="Last Name"
                                value={values.lastName}
                                helperText={touched.lastName && errors.lastName}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.lastName && errors.lastName
                                )}
                                className="mandatory-field"
                              />
                            </FormControl>
                          </Grid>
                          <Grid item md={6}>
                            <FormControl variant="standard" fullWidth>
                              <Input
                                type="email"
                                name="email"
                                label="Email"
                                value={values.email}
                                helperText={touched.email && errors.email}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(touched.email && errors.email)}
                                className="mandatory-field"
                              />
                            </FormControl>
                          </Grid>
                          <Grid item md={6} display="flex">
                            <FormControl variant="standard" fullWidth>
                              <Input
                                type="text"
                                name="designation"
                                label="Designation"
                                value={values.designation}
                                helperText={
                                  touched.designation && errors.designation
                                }
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.designation && errors.designation
                                )}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item md={6} sx={{mb: '18px'}}>
                            <FormControl variant="standard" fullWidth>
                              <Select
                                labelId="type"
                                name="type"
                                label="Type"
                                onChangeSelect={(...args) => {
                                  setValues(
                                    preState => ({
                                      ...preState,
                                      islandPartner:
                                        dataDetails?.merchantId ?? '',
                                      roleId: getSelectedRoleIdByRoleName(
                                        dataDetails?.roleName
                                      ),
                                    }),
                                    false
                                  );
                                  handleChange(...args);
                                }}
                                value={values.type.toString()}
                                selectOptions={Object.keys(USER_ROLE).reduce(
                                  (roles, key) => {
                                    roles.push({
                                      label: USER_ROLE[key],
                                      value: key,
                                    });
                                    return roles;
                                  },
                                  []
                                )}
                                onBlur={handleBlur}
                              />
                            </FormControl>
                          </Grid>
                          {values.type?.toString() ===
                            USER_TYPE.ADMINISTRATOR.toString() && (
                            <Grid item md={6} sx={{mb: '18px'}}>
                              <FormControl variant="standard" fullWidth>
                                <Select
                                  labelId="roleId"
                                  name="roleId"
                                  label="Role"
                                  onChangeSelect={(...args) => {
                                    handleChange(...args);
                                    handleBlur(...args);
                                  }}
                                  onBlur={handleBlur}
                                  value={values.roleId ?? ''}
                                  selectOptions={optionAdminRoles}
                                  helperText={touched.roleId && errors.roleId}
                                  error={Boolean(
                                    touched.roleId && errors.roleId
                                  )}
                                  disabled={disabledRoleOptions}
                                />
                              </FormControl>
                            </Grid>
                          )}
                          {values.type?.toString() ===
                            USER_TYPE.MERCHANT.toString() && (
                            <Grid item md={6} sx={{mb: '18px'}}>
                              <FormControl variant="standard" fullWidth>
                                <Select
                                  labelId="merchantId"
                                  name="merchantId"
                                  label="Island Partner"
                                  onChangeSelect={handleChange}
                                  onBlur={handleBlur}
                                  value={values.merchantId ?? ''}
                                  selectOptions={optionIslandPartners}
                                  error={Boolean(
                                    touched.merchantId && errors.merchantId
                                  )}
                                  helperText={
                                    touched.merchantId && errors.merchantId
                                  }
                                />
                              </FormControl>
                            </Grid>
                          )}
                          <Grid item md={6}>
                            <FormControl fullWidth>
                              <Input
                                type="phoneNumber"
                                name="phoneNumber"
                                label="Phone number (e.g. 9/6XXXXXXX)"
                                placeholder="Enter phone number (e.g. 9/6XXXXXXX)"
                                value={values.phoneNumber}
                                onChange={handleChange}
                                helperText={
                                  touched.phoneNumber && errors.phoneNumber
                                }
                                onBlur={handleBlur}
                                error={Boolean(
                                  touched.phoneNumber && errors.phoneNumber
                                )}
                                className="mandatory-field"
                              />
                            </FormControl>
                          </Grid>
                          <Grid item md={6}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginTop: '15px',
                              }}
                            >
                              <Typography variant="inherit">
                                Unfreeze Account
                              </Typography>
                              <Checkbox
                                name="isFreeze"
                                checked={values.isFreeze}
                                onChange={handleChange}
                                inputProps={{'aria-label': 'controlled'}}
                                disabled={!values.isFreeze}
                              />
                            </div>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Shadow>
                </Grid>
              </Grid>
              <FooterDetail
                isResetPassword
                isEdit={idDetail ? true : false}
                toggleModalConfirm={toggleModalConfirm}
                toggleModalConfirmDelete={toggleModalConfirmDelete}
                titleDelete="Delete Administrator"
                loading={isLoading}
                canExplicitlyAddOrSave={
                  initialValues?.type === USER_TYPE.ADMINISTRATOR
                    ? checkRbac({
                        permissionString: [
                          rbacPermissions.ADMINISTRATORS__LISTING__EDIT_ADMINISTRATORS,
                        ],
                      })
                    : initialValues?.type === USER_TYPE.MERCHANT
                    ? checkRbac({
                        permissionString: [
                          rbacPermissions.ADMINISTRATORS__LISTING__EDIT_MERCHANTS,
                        ],
                      })
                    : false
                }
                canExplicitlyDelete={
                  initialValues?.type === USER_TYPE.ADMINISTRATOR
                    ? checkRbac({
                        permissionString: [
                          rbacPermissions.ADMINISTRATORS__LISTING__DELETE_ADMINISTRATORS,
                        ],
                      })
                    : initialValues?.type === USER_TYPE.MERCHANT
                    ? checkRbac({
                        permissionString: [
                          rbacPermissions.ADMINISTRATORS__LISTING__DELETE_MERCHANTS,
                        ],
                      })
                    : false
                }
                canExplicitlyActionResetPassword={
                  initialValues?.type === USER_TYPE.ADMINISTRATOR
                    ? checkRbac({
                        permissionString: [
                          rbacPermissions.ADMINISTRATORS__LISTING__EDIT_ADMINISTRATORS,
                        ],
                      })
                    : initialValues?.type === USER_TYPE.MERCHANT
                    ? checkRbac({
                        permissionString: [
                          rbacPermissions.ADMINISTRATORS__LISTING__EDIT_MERCHANTS,
                        ],
                      })
                    : false
                }
              />
            </form>
          )}
        </Formik>
      </Box>
    </>
  );
}

export default SDCAdministratorsDetail;
